import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-06-15';

class BackInStockComponent extends Component {
  /** @type {boolean} */
  #loading = false;

  /** @type {AbortController} */
  #variantAbortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.publicKey = this.dataset.klaviyoPublicKey || '';
    this.catalogPrefix = this.dataset.catalogPrefix || '$shopify:::$default:::';
    this.variantId = this.dataset.initialVariantId || '';
    this.variantTitle = this.dataset.initialVariantTitle || '';
    this.inStock = this.dataset.initialVariantAvailable === 'true';
    this.forceVisible = this.dataset.forceVisible === 'true';

    if (!this.publicKey) {
      this.hidden = true;
      return;
    }

    this.refs.form?.addEventListener('submit', this.#handleSubmit);

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#variantAbortController.signal,
    });

    this.#updateVariantLabel();
    this.#toggleVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantAbortController.abort();
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const { resource, data } = event.detail;

    if (data.newProduct?.id) {
      this.dataset.productId = data.newProduct.id;
      if (data.newProduct.url) {
        this.dataset.productUrl = data.newProduct.url;
      }
    } else if (data.productId && data.productId !== this.dataset.productId) {
      return;
    }

    if (resource?.id) {
      this.variantId = resource.id.toString();
      this.variantTitle = resource.title || this.variantTitle;
      this.inStock = Boolean(resource.available);
      this.#updateVariantLabel();
      this.#toggleVisibility();
    } else {
      this.variantId = '';
      this.#toggleVisibility();
    }
  };

  #handleSubmit = (event) => {
    event.preventDefault();
    if (this.#loading) return;

    const email = this.refs.emailInput?.value.trim() || '';
    const phone = this.refs.phoneInput?.value.trim() || '';

    if (!email && !phone) {
      this.#showFeedback('error', this.dataset.errorMessage || 'Add an email or phone number to continue.');
      return;
    }

    if (!this.variantId) {
      this.#showFeedback('error', this.dataset.errorMessage || 'Please pick a variant.');
      return;
    }

    const payload = this.#buildPayload({ email, phone });

    this.#setLoading(true);
    fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.publicKey)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          let message = this.dataset.errorMessage || 'Unable to save your request.';
          try {
            const data = await response.json();
            message = data?.errors?.[0]?.detail || message;
          } catch (error) {
            // Ignore JSON parse issues
          }
          throw new Error(message);
        }

        this.refs.form?.reset();
        this.#showFeedback('success', this.dataset.successMessage || 'Thanks! We will notify you soon.');
      })
      .catch((error) => {
        this.#showFeedback('error', error?.message || this.dataset.errorMessage || 'Something went wrong.');
      })
      .finally(() => {
        this.#setLoading(false);
      });
  };

  /**
   * @param {{email: string, phone: string}} param0
   */
  #buildPayload({ email, phone }) {
    const channels = [];
    const profileAttributes = {};

    if (email) {
      channels.push('EMAIL');
      profileAttributes.email = email;
    }

    if (phone) {
      channels.push('SMS');
      profileAttributes.phone_number = phone;
    }

    const profile = {
      data: {
        type: 'profile',
        attributes: {
          ...profileAttributes,
          properties: {
            product_title: this.dataset.productTitle || '',
            product_url: this.dataset.productUrl || window.location.href,
            variant_title: this.variantTitle || '',
          },
        },
      },
    };

    /** @type {Record<string, any>} */
    const attributes = {
      channels,
      profile,
    };

    if (this.dataset.customSource) {
      attributes.custom_source = this.dataset.customSource;
    }

    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes,
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `${this.catalogPrefix}${this.variantId}`,
            },
          },
        },
      },
    };
  }

  #toggleVisibility() {
    const shouldShow = this.forceVisible || (!this.inStock && Boolean(this.variantId));
    this.hidden = !shouldShow;

    if (!shouldShow) {
      this.#resetFeedback();
    }
  }

  #updateVariantLabel() {
    if (this.refs.variantLabel && this.variantTitle) {
      this.refs.variantLabel.textContent = this.variantTitle;
    }
  }

  /**
   * @param {'success' | 'error'} state
   * @param {string} message
   */
  #showFeedback(state, message) {
    if (!this.refs.feedback) return;
    this.refs.feedback.dataset.state = state;
    this.refs.feedback.textContent = message;
    this.refs.feedback.hidden = false;
  }

  #resetFeedback() {
    if (!this.refs.feedback) return;
    this.refs.feedback.hidden = true;
    delete this.refs.feedback.dataset.state;
    this.refs.feedback.textContent = '';
  }

  #setLoading(isLoading) {
    this.#loading = isLoading;
    if (this.refs.submitButton) {
      this.refs.submitButton.disabled = isLoading;
      this.refs.submitButton.classList.toggle('is-loading', isLoading);
    }
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
